import { useId, useState } from 'react';
import {
  Result,
  setDesiredFirmwareAndTryUpgrade,
} from '../../api/api';
import { IFirmware } from '../../api/types';
import { Button } from '../Common/Button';
import { StatusField } from '../Common/StatusLabel';
import { getMaxDevicesPerRequest } from './firmwareWorkflow';
import {
  FirmwareRolloutBatchProgress,
  runFirmwareRolloutBatches,
} from './firmwareRolloutBatches';
import { StepProgress } from './StepProgress';

interface Params {
  productCode: string;
  firmware: IFirmware;
  defaultDevicesToUpgrade?: number;
  variant: string;
  disabled?: boolean;
  disabledReason?: string;
  maxDevicesPerRequest?: number;
  onSubmitted?: () => void;
}

export function PrepareNumberOfDevicesForFirmware({
  productCode,
  firmware,
  defaultDevicesToUpgrade = 0,
  variant,
  disabled = false,
  disabledReason,
  maxDevicesPerRequest = getMaxDevicesPerRequest(firmware),
  onSubmitted,
}: Params) {
  const [devicesToUpgrade, setDevicesToUpgrade] = useState<string>(
    defaultDevicesToUpgrade.toString(),
  );
  const [firmwareAsDesiredResult, setFirmwareAsDesiredResult] =
    useState<Result<string>>({
      status: '',
    });
  const [rolloutProgress, setRolloutProgress] =
    useState<FirmwareRolloutBatchProgress | null>(null);
  const [isRolloutInProgress, setIsRolloutInProgress] =
    useState(false);
  const inputId = useId();
  const validationId = `${inputId}-validation`;

  const parsedDevicesToUpgrade = Number(devicesToUpgrade);
  const isValidNumberOfDevices =
    Number.isInteger(parsedDevicesToUpgrade) &&
    parsedDevicesToUpgrade > 0 &&
    (maxDevicesPerRequest === undefined ||
      parsedDevicesToUpgrade <= maxDevicesPerRequest);
  const validationMessage =
    devicesToUpgrade.trim() === ''
      ? 'Enter the number of devices to update.'
      : !Number.isInteger(parsedDevicesToUpgrade) ||
          parsedDevicesToUpgrade <= 0
        ? 'Enter a positive whole number of devices.'
        : maxDevicesPerRequest !== undefined &&
            parsedDevicesToUpgrade > maxDevicesPerRequest
          ? `Enter ${maxDevicesPerRequest} devices or fewer.`
          : undefined;

  const setDesiredFirmwareAndTryUpgradeFunc = async () => {
    if (
      disabled ||
      !isValidNumberOfDevices ||
      firmwareAsDesiredResult.status === 'Loading'
    ) {
      return;
    }

    setFirmwareAsDesiredResult({ status: 'Loading' });
    setIsRolloutInProgress(true);
    const result = await runFirmwareRolloutBatches(
      parsedDevicesToUpgrade,
      (batchSize) =>
        setDesiredFirmwareAndTryUpgrade(
          productCode,
          batchSize,
          firmware.firmwareId,
          variant,
        ),
      setRolloutProgress,
    );
    setFirmwareAsDesiredResult(result);
    setIsRolloutInProgress(false);
    if (result.status === 'OK') {
      onSubmitted?.();
    }
  };

  const closeRolloutProgress = () => {
    if (isRolloutInProgress) {
      return;
    }

    setRolloutProgress(null);
    setFirmwareAsDesiredResult({ status: '' });
  };

  if (rolloutProgress) {
    const isSuccessful = firmwareAsDesiredResult.status === 'OK';

    return (
      <div className="flex flex-col content-center items-center gap-3">
        <div
          className="text-sm font-medium"
          role={isRolloutInProgress ? 'status' : undefined}
          aria-live="polite"
        >
          {isRolloutInProgress
            ? 'Sending firmware rollout'
            : isSuccessful
              ? 'Firmware rollout complete'
              : 'Firmware rollout stopped'}
        </div>
        <div className="w-full max-w-xl">
          <StepProgress
            currentCount={rolloutProgress.completedDevices}
            targetCount={rolloutProgress.totalDevices}
          />
        </div>
        <div className="text-xs font-thin">
          Batch {rolloutProgress.currentBatch} of{' '}
          {rolloutProgress.totalBatches}.{' '}
          {rolloutProgress.completedDevices} of{' '}
          {rolloutProgress.totalDevices} devices submitted.
        </div>
        {!isRolloutInProgress && !isSuccessful && (
          <div className="text-xs font-thin" role="alert">
            No further batches were sent. Verify the desired-device
            count before starting another rollout.
          </div>
        )}
        <StatusField
          result={firmwareAsDesiredResult}
          successMessage={`Desired firmware saved for ${rolloutProgress.totalDevices} devices.`}
        />
        {!isRolloutInProgress && (
          <Button
            onClick={closeRolloutProgress}
            text={isSuccessful ? 'Prepare another rollout' : 'Back'}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col content-center items-center">
      <div className="flex-row mt-2">
        <label htmlFor={inputId} className="mr-2">
          Number of devices
        </label>
        <input
          id={inputId}
          type="number"
          min={1}
          max={maxDevicesPerRequest}
          aria-invalid={!isValidNumberOfDevices}
          aria-describedby={
            validationMessage ? validationId : undefined
          }
          className="bg-purple-white rounded border-2 border-grey p-3 text-sm mr-2"
          disabled={
            disabled || firmwareAsDesiredResult.status === 'Loading'
          }
          value={devicesToUpgrade}
          onChange={(v) => setDevicesToUpgrade(v.currentTarget.value)}
        />
        <Button
          onClick={() => setDesiredFirmwareAndTryUpgradeFunc()}
          text={'Send'}
          disabled={[
            {
              disabled: disabled,
              reason:
                disabledReason ??
                'This firmware step is not available yet.',
            },
            {
              disabled: firmwareAsDesiredResult.status === 'Loading',
              reason: 'A request is already being submitted.',
            },
            {
              disabled: !isValidNumberOfDevices,
              reason:
                validationMessage ??
                'Enter a valid number of devices.',
            },
          ]}
        />
      </div>
      {validationMessage && !disabled && (
        <div
          id={validationId}
          className="text-xs text-red-600 mt-2"
          role="alert"
        >
          {validationMessage}
        </div>
      )}
      <StatusField
        result={firmwareAsDesiredResult}
        successMessage="Desired firmware saved. Device upgrade outcome is not yet confirmed."
      />
    </div>
  );
}
